import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

const AnalyticsContext = createContext();

export const useAnalytics = () => {
  const context = useContext(AnalyticsContext);
  if (!context) {
    throw new Error('useAnalytics must be used within an AnalyticsProvider');
  }
  return context;
};

export const AnalyticsProvider = ({ children }) => {
  const [config, setConfig] = useState(null);
  const [isLoaded, setIsLoaded] = useState(false);
  const [consentGiven, setConsentGiven] = useState(false);
  const [debugMode, setDebugMode] = useState(false);
  const { user } = useAuth();

  // Load analytics configuration from server
  const loadConfig = useCallback(async () => {
    try {
      const response = await axios.get('/api/analytics/config');
      if (response.data.success) {
        const analyticsConfig = response.data.data;
        setConfig(analyticsConfig);
        setDebugMode(analyticsConfig.ga4?.debug_mode || false);
        
        // Check if consent is required
        if (analyticsConfig.privacy?.cookie_consent_required) {
          // Check if consent was previously given
          const consent = localStorage.getItem('analytics_consent');
          setConsentGiven(consent === 'true');
        } else {
          setConsentGiven(true); // Auto-consent if not required
        }
        
        // Mark as loaded immediately after config is loaded
        setIsLoaded(true);
        
      }
    } catch (error) {
      console.error('Failed to load analytics config:', error);
      // Even if config fails to load, mark as loaded to prevent blocking
      setIsLoaded(true);
    }
  }, []);

  // Initialize Google Analytics 4
  const initializeGA4 = useCallback(() => {
    if (!config?.analytics_enabled || !config?.ga4?.enabled || !config?.ga4?.measurement_id) {
      return;
    }

    if (!consentGiven) {
      return;
    }

    // Load gtag script
    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.googletagmanager.com/gtag/js?id=${config.ga4.measurement_id}`;
    document.head.appendChild(script);

    // Initialize gtag
    window.dataLayer = window.dataLayer || [];
    function gtag() {
      window.dataLayer.push(arguments);
    }
    window.gtag = gtag;

    gtag('js', new Date());
    
    // Configure GA4
    const configOptions = {
      // Privacy settings
      anonymize_ip: config.ga4.anonymize_ip,
      allow_google_signals: !config.privacy?.gdpr_compliance,
      allow_ad_personalization_signals: !config.privacy?.gdpr_compliance,
      
      // Debug mode
      debug_mode: config.ga4.debug_mode,
      
      // Enhanced ecommerce
      send_page_view: true,
      
      // Custom parameters
      custom_map: {
        custom_parameter_1: 'user_type',
        custom_parameter_2: 'page_category'
      }
    };

    gtag('config', config.ga4.measurement_id, configOptions);
    
  }, [config, consentGiven]);

  // Initialize Facebook Pixel
  const initializeFacebookPixel = useCallback(() => {
    if (!config?.analytics_enabled || !config?.facebook_pixel?.enabled || !config?.facebook_pixel?.pixel_id) {
      return;
    }

    if (!consentGiven) {
      return;
    }

    // Facebook Pixel Code
    (function(f,b,e,v,n,t,s)
    {if(f.fbq)return;n=f.fbq=function(){n.callMethod?
    n.callMethod.apply(n,arguments):n.queue.push(arguments)};
    if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';
    n.queue=[];t=b.createElement(e);t.async=!0;
    t.src=v;s=b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t,s)})(window, document,'script',
    'https://connect.facebook.net/en_US/fbevents.js');

    window.fbq('init', config.facebook_pixel.pixel_id);
    window.fbq('track', 'PageView');

  }, [config, consentGiven]);

  // Initialize Microsoft Clarity
  const initializeMicrosoftClarity = useCallback(() => {
    if (!config?.analytics_enabled || !config?.heatmaps?.enabled || !config?.heatmaps?.microsoft_clarity_id) {
      return;
    }

    if (!consentGiven) {
      return;
    }

    (function(c,l,a,r,i,t,y){
        c[a]=c[a]||function(){(c[a].q=c[a].q||[]).push(arguments)};
        t=l.createElement(r);t.async=1;t.src="https://www.clarity.ms/tag/"+i;
        y=l.getElementsByTagName(r)[0];y.parentNode.insertBefore(t,y);
    })(window, document, "clarity", "script", config.heatmaps.microsoft_clarity_id);

  }, [config, consentGiven]);

  // Initialize Hotjar
  const initializeHotjar = useCallback(() => {
    if (!config?.analytics_enabled || !config?.heatmaps?.enabled || !config?.heatmaps?.hotjar_site_id) {
      return;
    }

    if (!consentGiven) {
      return;
    }

    (function(h,o,t,j,a,r){
        h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
        h._hjSettings={hjid:parseInt(config.heatmaps.hotjar_site_id),hjsv:6};
        a=o.getElementsByTagName('head')[0];
        r=o.createElement('script');r.async=1;
        r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
        a.appendChild(r);
    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');

  }, [config, consentGiven]);

  // Grant consent
  const grantConsent = useCallback(() => {
    setConsentGiven(true);
    localStorage.setItem('analytics_consent', 'true');
  }, []);

  // Revoke consent
  const revokeConsent = useCallback(() => {
    setConsentGiven(false);
    localStorage.removeItem('analytics_consent');
    
    // Clear existing tracking
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'denied',
        ad_storage: 'denied'
      });
    }
    
  }, []);

  // Track page view
  const trackPageView = useCallback((page_title, page_location, page_path) => {
    if (!isLoaded || !consentGiven) return;

    if (window.gtag && config?.ga4?.enabled) {
      window.gtag('event', 'page_view', {
        page_title,
        page_location,
        page_path,
        send_to: config.ga4.measurement_id
      });
    }

    if (window.fbq && config?.facebook_pixel?.enabled) {
      window.fbq('track', 'PageView');
    }

    if (debugMode) {
    }
  }, [isLoaded, consentGiven, config, debugMode]);

  // Helper function to get Facebook endpoint for server-side tracking
  const getFacebookEndpoint = useCallback((eventName) => {
    const endpointMap = {
      'page_view': 'page-view',
      'view_item': 'view-content',
      'add_to_cart': 'add-to-cart',
      'add_to_wishlist': 'add-to-wishlist',
      'begin_checkout': 'initiate-checkout',
      'purchase': 'purchase',
      'search': 'search',
      'sign_up': 'complete-registration'
    };
    return endpointMap[eventName] || 'custom-event';
  }, []);

  // Track custom event
  const trackEvent = useCallback(async (event_name, parameters = {}) => {
    if (!isLoaded || !consentGiven) return;

    // Client-side GA4 tracking
    if (window.gtag && config?.ga4?.enabled) {
      window.gtag('event', event_name, {
        ...parameters,
        send_to: config.ga4.measurement_id
      });
    }

    // Client-side Facebook Pixel tracking
    if (window.fbq && config?.facebook_pixel?.enabled) {
      const fbEventMap = {
        'purchase': 'Purchase',
        'add_to_cart': 'AddToCart',
        'view_item': 'ViewContent',
        'begin_checkout': 'InitiateCheckout',
        'search': 'Search',
        'sign_up': 'CompleteRegistration',
        'login': 'Login'
      };

      const fbEvent = fbEventMap[event_name] || event_name;
      window.fbq('track', fbEvent, parameters);
    }

    // Server-side Facebook Conversions API tracking
    if (config?.facebook_pixel?.enabled) {
      try {
        const endpoint = getFacebookEndpoint(event_name);
        const serverEventData = {
          user_data: user ? {
            email: user.email,
            firstName: user.first_name,
            lastName: user.last_name,
            phone: user.phone
          } : {},
          page_url: window.location.href,
          ...parameters
        };

        if (endpoint === 'custom-event') {
          serverEventData.event_name = event_name;
          serverEventData.custom_data = parameters;
        }

        await axios.post(`/api/facebook-analytics/${endpoint}`, serverEventData);
        
        if (debugMode) {
        }
      } catch (error) {
        console.error('📊 Facebook server-side tracking error:', error);
      }
    }

    if (debugMode) {
    }
  }, [isLoaded, consentGiven, config, debugMode, user, getFacebookEndpoint]);

  // Track ecommerce events
  const trackPurchase = useCallback((transaction_id, value, currency, items) => {
    trackEvent('purchase', {
      transaction_id,
      value,
      currency,
      items
    });
  }, [trackEvent]);

  const trackAddToCart = useCallback((currency, value, items) => {
    trackEvent('add_to_cart', {
      currency,
      value,
      items
    });
  }, [trackEvent]);

  const trackViewItem = useCallback((currency, value, items) => {
    trackEvent('view_item', {
      currency,
      value,
      items
    });
  }, [trackEvent]);

  const trackBeginCheckout = useCallback((currency, value, items) => {
    trackEvent('begin_checkout', {
      currency,
      value,
      items
    });
  }, [trackEvent]);

  // Load config on mount
  useEffect(() => {
    loadConfig();
  }, [loadConfig]);

  // Initialize analytics when config and consent are ready
  useEffect(() => {
    if (config && consentGiven) {
      initializeGA4();
      initializeFacebookPixel();
      initializeMicrosoftClarity();
      initializeHotjar();
    }
  }, [config, consentGiven, initializeGA4, initializeFacebookPixel, initializeMicrosoftClarity, initializeHotjar]);

  const value = {
    // State
    config,
    isLoaded,
    consentGiven,
    debugMode,
    
    // Methods
    grantConsent,
    revokeConsent,
    trackPageView,
    trackEvent,
    
    // Ecommerce methods
    trackPurchase,
    trackAddToCart,
    trackViewItem,
    trackBeginCheckout,
    
    // Utility
    loadConfig
  };

  return (
    <AnalyticsContext.Provider value={value}>
      {children}
    </AnalyticsContext.Provider>
  );
};

export default AnalyticsProvider;
